import { CHANGELOG_ENTRIES } from "./entries";
import type { ChangelogEntry } from "./types";
import { compareVersions, isValidVersion } from "./version";

export * from "./types";
export { compareVersions, isValidVersion } from "./version";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Valida la estructura de las entradas. Devuelve la lista de problemas (vacía
 * si todo está bien). Se ejecuta en los tests de la API, así un error de
 * formato se detecta en CI y no en producción.
 */
export function validateChangelog(entries: readonly ChangelogEntry[]): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();

  entries.forEach((entry, index) => {
    const label = `entrada #${index + 1} (${String(entry.version)})`;
    if (!isValidVersion(entry.version)) errors.push(`${label}: versión inválida, se espera X.Y.Z`);
    if (seen.has(entry.version)) errors.push(`${label}: versión repetida`);
    seen.add(entry.version);

    if (entry.fecha !== null && !DATE_RE.test(entry.fecha)) {
      errors.push(`${label}: fecha inválida, se espera YYYY-MM-DD o null`);
    }
    if (entry.items.length === 0) errors.push(`${label}: sin ítems`);
    entry.items.forEach((item, i) => {
      if (!item.titulo.trim()) errors.push(`${label}: ítem #${i + 1} sin título`);
    });

    const previous = entries[index - 1];
    if (previous && compareVersions(previous.version, entry.version) <= 0) {
      errors.push(`${label}: las entradas deben ir de la versión más nueva a la más vieja`);
    }
  });

  return errors;
}

/** Entradas ordenadas de la más nueva a la más vieja (el orden del archivo). */
export const CHANGELOG: readonly ChangelogEntry[] = CHANGELOG_ENTRIES;

/** Versión de la entrada más reciente, o null si no hay entradas. */
export function latestChangelogVersion(entries: readonly ChangelogEntry[] = CHANGELOG): string | null {
  return entries[0]?.version ?? null;
}

export function isKnownChangelogVersion(version: string, entries: readonly ChangelogEntry[] = CHANGELOG): boolean {
  return entries.some((entry) => entry.version === version);
}

/**
 * Entradas que el usuario todavía no vio. `lastSeen` es la última versión que
 * marcó como vista (null para un usuario que nunca abrió Novedades: ve todo
 * como nuevo, que es lo que queremos la primera vez).
 */
export function unreadChangelogEntries(
  lastSeen: string | null | undefined,
  entries: readonly ChangelogEntry[] = CHANGELOG,
): ChangelogEntry[] {
  if (!lastSeen || !isValidVersion(lastSeen)) return [...entries];
  return entries.filter((entry) => compareVersions(entry.version, lastSeen) > 0);
}

export function hasUnreadChangelog(
  lastSeen: string | null | undefined,
  entries: readonly ChangelogEntry[] = CHANGELOG,
): boolean {
  return unreadChangelogEntries(lastSeen, entries).length > 0;
}
